import * as React from 'react';
import { Check, ChevronDown } from 'lucide-react';
import {
  filterSearchableSelectOptions,
  type SearchableSelectLabels,
  type SearchableSelectOption,
} from '@repo/ui-contracts';
import { cn } from '../lib/utils';
import { Input } from './input';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

export type { SearchableSelectLabels, SearchableSelectOption } from '@repo/ui-contracts';

const popoverContentStyle: React.CSSProperties = {
  minWidth: 'var(--radix-popover-trigger-width)',
  maxWidth: 'calc(100vw - 2rem)',
};

const sectionLabelStyle: React.CSSProperties = {
  letterSpacing: 'var(--type-label-tracking)',
};

const listboxStyle: React.CSSProperties = {
  maxHeight:
    'min(18rem, calc(100vh - 6rem), max(0px, calc(var(--radix-popover-content-available-height) - 4.5rem)))',
};

type SearchableSelectProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'value' | 'onChange'
> & {
  value: string | null;
  options: SearchableSelectOption[];
  shortcutOptions?: SearchableSelectOption[];
  placeholder?: string;
  labels: SearchableSelectLabels;
  triggerIcon?: React.ReactNode;
  onValueChange: (value: string) => void;
  contentClassName?: string;
  searchInputClassName?: string;
};

export function SearchableSelect({
  value,
  options,
  shortcutOptions = [],
  placeholder,
  labels,
  triggerIcon,
  onValueChange,
  className,
  contentClassName,
  searchInputClassName,
  disabled,
  type = 'button',
  ...props
}: SearchableSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const selectedOption = React.useMemo(
    () => options.find((option) => option.value === value) ?? null,
    [options, value],
  );
  const filteredOptions = React.useMemo(
    () => filterSearchableSelectOptions(options, query),
    [options, query],
  );
  const showShortcutOptions = query.trim().length === 0 && shortcutOptions.length > 0;
  const displayValue = selectedOption?.triggerLabel ?? selectedOption?.label ?? placeholder ?? '';
  const hasValue = selectedOption !== null;

  React.useEffect(() => {
    if (!open) {
      setQuery('');
    }
  }, [open]);

  const handleSelect = (nextValue: string) => {
    onValueChange(nextValue);
    setOpen(false);
  };

  const renderOption = (option: SearchableSelectOption) => {
    const selected = option.value === value;

    return (
      <button
        key={option.value}
        type="button"
        role="option"
        aria-selected={selected}
        className={cn(
          'flex w-full items-start justify-between rounded-md px-3 py-2 text-left text-sm text-foreground transition hover:bg-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/25',
          selected ? 'bg-accent/40' : undefined,
        )}
        onClick={() => handleSelect(option.value)}
      >
        <span className="min-w-0 flex-1 break-words pr-2 leading-5 whitespace-normal">
          {option.label}
        </span>
        <span className="ml-3 mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-primary">
          {selected ? <Check className="h-4 w-4" /> : null}
        </span>
      </button>
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type={type}
          className={cn(
            'flex h-10 w-full cursor-pointer items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-left text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/25 disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
          aria-label={labels.trigger}
          aria-expanded={open}
          disabled={disabled}
          {...props}
        >
          <span className={cn('truncate', hasValue ? 'text-foreground' : 'text-muted-foreground')}>
            {displayValue}
          </span>
          <span className="ml-3 flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground">
            {triggerIcon ?? <ChevronDown className="h-4 w-4" />}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn('w-80 overflow-hidden p-0', contentClassName)}
        style={popoverContentStyle}
      >
        <div className="border-b border-border p-2">
          <Input
            type="search"
            size="md"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onClear={() => setQuery('')}
            placeholder={labels.searchPlaceholder}
            aria-label={labels.searchLabel}
            clearButtonLabel={labels.clearSearch}
            className={searchInputClassName}
          />
        </div>
        <div
          className="overflow-y-auto overscroll-contain p-2 [scrollbar-gutter:stable]"
          role="listbox"
          aria-label={labels.trigger}
          style={listboxStyle}
        >
          {showShortcutOptions ? (
            <div className="space-y-1">
              {labels.shortcuts ? (
                <p
                  className="px-3 pb-1 pt-1 text-xs font-medium uppercase text-muted-foreground"
                  style={sectionLabelStyle}
                >
                  {labels.shortcuts}
                </p>
              ) : null}
              {shortcutOptions.map(renderOption)}
            </div>
          ) : null}
          {showShortcutOptions && filteredOptions.length > 0 ? (
            <div className="my-2 border-t border-border" />
          ) : null}
          {filteredOptions.length > 0 ? (
            <div className="space-y-1">
              {showShortcutOptions && labels.options ? (
                <p
                  className="px-3 pb-1 pt-1 text-xs font-medium uppercase text-muted-foreground"
                  style={sectionLabelStyle}
                >
                  {labels.options}
                </p>
              ) : null}
              {filteredOptions.map(renderOption)}
            </div>
          ) : (
            <p className="px-3 py-4 text-sm text-muted-foreground">{labels.empty}</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
